/*
 * Host-aware Applied SuperIntelligence document branding.
 *
 * Assets live in public/brand/asi/. The delivered lockup is logo-lockup.png
 * (landscape gold/white mark on transparent). favicon-64 / app-icon-180 /
 * og-image are navy-backed PNGs derived from that lockup.
 * Prefer PNG for the primary favicon until a true SVG wordmark exists.
 *
 * Mirrors src/features/decisionPoint/marketing/fusionMsiBranding.js.
 */

const BRAND_BASE = "/brand/asi";
const FAVICON_PNG = `${BRAND_BASE}/favicon-64.png`;
const FAVICON_SVG = `${BRAND_BASE}/favicon.svg`;
const APPLE_ICON = `${BRAND_BASE}/app-icon-180.png`;
const SOCIAL_IMAGE = `${BRAND_BASE}/og-image.png`;

/** Find or create a <link rel> in <head>; returns the element + created flag. */
function ensureLink(selector, create) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/** Find or create a <meta> by name/property; returns the element + created flag. */
function ensureMeta(attr, value) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/**
 * Apply ASI favicon + apple-touch + social meta. Returns a cleanup that
 * restores the prior document head state.
 */
export function applyAsiBranding() {
  const restores = [];

  const icon = ensureLink('link[rel="icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "icon");
    return l;
  });
  const prevIconHref = icon.el.getAttribute("href");
  const prevIconType = icon.el.getAttribute("type");
  icon.el.setAttribute("type", "image/png");
  icon.el.setAttribute("href", FAVICON_PNG);
  restores.push(() => {
    if (icon.created) {
      icon.el.remove();
    } else {
      if (prevIconHref != null) icon.el.setAttribute("href", prevIconHref);
      if (prevIconType != null) icon.el.setAttribute("type", prevIconType);
      else icon.el.removeAttribute("type");
    }
  });

  const iconSvg = ensureLink('link[rel="alternate icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate icon");
    l.setAttribute("type", "image/svg+xml");
    return l;
  });
  const prevIconSvgHref = iconSvg.el.getAttribute("href");
  iconSvg.el.setAttribute("href", FAVICON_SVG);
  restores.push(() => {
    if (iconSvg.created) iconSvg.el.remove();
    else if (prevIconSvgHref != null) iconSvg.el.setAttribute("href", prevIconSvgHref);
  });

  const apple = ensureLink('link[rel="apple-touch-icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "apple-touch-icon");
    return l;
  });
  const prevAppleHref = apple.el.getAttribute("href");
  apple.el.setAttribute("href", APPLE_ICON);
  restores.push(() => {
    if (apple.created) apple.el.remove();
    else if (prevAppleHref != null) apple.el.setAttribute("href", prevAppleHref);
  });

  const socialUrl = `${window.location.origin}${SOCIAL_IMAGE}`;
  const metaPairs = [
    ["property", "og:image"],
    ["name", "twitter:image"],
    ["name", "twitter:card"],
  ];
  const metaValues = {
    "og:image": socialUrl,
    "twitter:image": socialUrl,
    "twitter:card": "summary_large_image",
  };
  for (const [attr, key] of metaPairs) {
    const { el, created } = ensureMeta(attr, key);
    const prevContent = el.getAttribute("content");
    el.setAttribute("content", metaValues[key]);
    restores.push(() => {
      if (created) el.remove();
      else if (prevContent != null) el.setAttribute("content", prevContent);
      else el.removeAttribute("content");
    });
  }

  return () => {
    for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
  };
}
